import { useState, useEffect, useRef, useCallback } from 'react';
import { apiFetch } from '../utils/streamApi';
import '../css/parvekshak.css';

const PRESETS = [
  { label: '7d',  days: 7  },
  { label: '14d', days: 14 },
  { label: '30d', days: 30 },
  { label: '90d', days: 90 },
  { label: 'All', days: 0  },
];

const FA_LABELS = {
  fol:  '🌿 Foliage',
  rain: '🌧 Rain / Fog',
  ins:  '🦟 Insect',
  car:  '🚗 Headlights',
  aml:  '🐾 Animal',
  fan:  '💨 Fan / Rotating',
  sta:  '🗿 Static Object',
  shd:  '🌑 Shadow',
  oth:  '❓ Other',
};

const fmtRangeDate = d => {
  const y  = d.getFullYear();
  const m  = String(d.getMonth() + 1).padStart(2, '0');
  const dy = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dy}`;
};

// preset (days, or -1 for custom) + date inputs → query string for the analysis endpoints.
function rangeQuery(preset, dateFrom, dateTo) {
  if (preset === -1) {
    const lo = Math.floor(new Date(dateFrom + 'T00:00:00').getTime() / 1000);
    const hi = Math.floor(new Date(dateTo   + 'T23:59:59').getTime() / 1000);
    return `ts_lo=${lo}&ts_hi=${hi}`;
  }
  return `days=${preset}`;
}

// Shared preset + custom-date toolbar used by every analysis tab.
function RangeToolbar({ preset, setPreset, dateFrom, setDateFrom, dateTo, setDateTo,
                       onRun, loading, children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '12px 0 16px' }}>
      <div style={{ display: 'flex', gap: 4 }}>
        {PRESETS.map(p => (
          <button key={p.days} className={`pk-filter-tab${preset === p.days ? ' active' : ''}`}
            onClick={() => setPreset(p.days)}>{p.label}</button>
        ))}
        <button className={`pk-filter-tab${preset === -1 ? ' active' : ''}`}
          onClick={() => setPreset(-1)}>Custom</button>
      </div>
      {preset === -1 && (
        <div className="pk-date-filter">
          <label>From</label>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
          <label>To</label>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} />
        </div>
      )}
      {children}
      <button className="pk-btn primary" onClick={onRun} disabled={loading} style={{ marginLeft: 'auto' }}>
        {loading ? '⏳ Running…' : '▶ Run Analysis'}
      </button>
    </div>
  );
}

// ─── Camera picker ────────────────────────────────────────────────────────────

function CamPicker({ cameras, value, onChange }) {
  const [open,   setOpen]   = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 0 });
  const trigRef = useRef(null);
  const menuRef = useRef(null);

  const openMenu = () => {
    if (trigRef.current) {
      const r = trigRef.current.getBoundingClientRect();
      setCoords({ top: r.bottom + 6, left: r.left, width: Math.max(r.width, 180) });
    }
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;
    const h = e => {
      if (trigRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  return (
    <>
      <button ref={trigRef}
        className={`alrt-cam-trigger${open ? ' open' : ''}${value ? ' filtered' : ''}`}
        onClick={() => open ? setOpen(false) : openMenu()}>
        <span>📷</span>
        <span className="alrt-cam-label">{value || 'All Cameras'}</span>
        <svg width="9" height="9" viewBox="0 0 9 9"
          style={{ flexShrink: 0, transition: 'transform 0.2s', transform: open ? 'rotate(180deg)' : 'none' }}>
          <path d="M1.5 3l3 3 3-3" stroke="currentColor" strokeWidth="1.5"
            strokeLinecap="round" strokeLinejoin="round" fill="none"/>
        </svg>
      </button>
      {open && (
        <div ref={menuRef} className="alrt-cam-menu"
          style={{ position: 'fixed', top: coords.top, left: coords.left,
            minWidth: coords.width, zIndex: 9999 }}>
          {[null, ...cameras].map(cam => (
            <button key={cam ?? '__all'} className={`alrt-cam-item${value === cam ? ' active' : ''}`}
              onClick={() => { onChange(cam); setOpen(false); }}>
              <span>{cam ? '🎥' : '📷'}</span>
              <span className="alrt-cam-item-label">{cam || 'All Cameras'}</span>
              {value === cam && <span className="alrt-cam-check">✓</span>}
            </button>
          ))}
        </div>
      )}
    </>
  );
}

// ─── Display primitives ───────────────────────────────────────────────────────

function StatCard({ label, value, color, sub }) {
  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border)',
      borderRadius: 12, padding: '14px 18px', minWidth: 110, flex: 1 }}>
      <div style={{ fontSize: 26, fontWeight: 800, color: color || 'var(--text)', lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>{label}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function Section({ title, children, accent }) {
  return (
    <div style={{ background: 'var(--surface)', border: `1px solid ${accent || 'var(--border)'}`,
      borderRadius: 12, overflow: 'hidden', marginBottom: 14 }}>
      <div style={{ padding: '11px 16px', borderBottom: '1px solid var(--border)',
        background: accent ? `${accent}18` : 'transparent',
        fontSize: 13, fontWeight: 700, color: accent || 'var(--text)' }}>
        {title}
      </div>
      <div style={{ padding: '14px 16px' }}>{children}</div>
    </div>
  );
}

function BarRow({ label, count, total, tag, tagColor, entryIds }) {
  const [expanded, setExpanded] = useState(false);
  const pct = total > 0 ? (count / total) * 100 : 0;
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
        <span style={{ fontSize: 12, color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 6 }}>
          {label}
          {tag && (
            <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 8,
              background: `${tagColor || '#e67e22'}22`, color: tagColor || '#e67e22',
              border: `1px solid ${tagColor || '#e67e22'}44` }}>
              {tag}
            </span>
          )}
        </span>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {entryIds?.length > 0 && (
            <button onClick={() => setExpanded(v => !v)}
              style={{ fontSize: 10, color: 'var(--primary)', background: 'transparent',
                border: 'none', cursor: 'pointer', padding: '1px 4px' }}>
              {expanded ? '▲ hide' : `▼ ${entryIds.length} entries`}
            </button>
          )}
          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text)' }}>{count}</span>
        </div>
      </div>
      <div style={{ height: 5, borderRadius: 3, background: 'var(--border)', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: 'var(--primary)',
          borderRadius: 3, transition: 'width 0.4s' }}/>
      </div>
      {expanded && entryIds?.length > 0 && (
        <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {entryIds.map(id => (
            <code key={id} style={{ fontSize: 10, padding: '2px 6px', borderRadius: 6,
              background: 'var(--surface-alt)', border: '1px solid var(--border)',
              color: 'var(--text-muted)' }}>
              {id.replace(/^missed_/, '')}
            </code>
          ))}
        </div>
      )}
    </div>
  );
}

function ScoreRange({ label, stats, color }) {
  if (!stats) return null;
  return (
    <span style={{ fontSize: 11, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
      {label}
      <code style={{ fontSize: 11, color: color || 'var(--text)', fontFamily: 'monospace' }}>
        {stats.min.toFixed(3)} – {stats.max.toFixed(3)}
      </code>
      <span style={{ color: 'var(--text-muted)' }}>(mean {stats.mean.toFixed(3)})</span>
    </span>
  );
}

// ─── Known tab components ─────────────────────────────────────────────────────

function ScoreDist({ genuineDist, impostorDist }) {
  const maxVal = Math.max(...genuineDist, ...impostorDist, 1);
  const labels = genuineDist.map((_, i) => `${(i * 10)}–${(i * 10 + 10)}%`);
  return (
    <div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 8, fontSize: 11 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ width: 10, height: 10, borderRadius: 2, background: 'rgba(34,197,94,0.7)', display: 'inline-block' }} />
          Genuine (known people)
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ width: 10, height: 10, borderRadius: 2, background: 'rgba(239,68,68,0.7)', display: 'inline-block' }} />
          Impostor (unknowns)
        </span>
      </div>
      <div style={{ display: 'flex', gap: 2, alignItems: 'flex-end', height: 80 }}>
        {genuineDist.map((g, i) => {
          const imp = impostorDist[i] || 0;
          return (
            <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column',
              alignItems: 'center', gap: 1, position: 'relative' }}>
              <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 1,
                alignItems: 'stretch', height: 70, justifyContent: 'flex-end' }}>
                {g > 0 && (
                  <div title={`Genuine: ${g}`}
                    style={{ background: 'rgba(34,197,94,0.7)', height: `${(g / maxVal) * 68}px`,
                      borderRadius: '2px 2px 0 0', minHeight: 2 }} />
                )}
                {imp > 0 && (
                  <div title={`Impostor: ${imp}`}
                    style={{ background: 'rgba(239,68,68,0.6)', height: `${(imp / maxVal) * 68}px`,
                      borderRadius: '2px 2px 0 0', minHeight: 2, marginTop: -2, opacity: 0.85 }} />
                )}
              </div>
              <div style={{ fontSize: 8, color: 'var(--text-muted)', writingMode: 'vertical-rl',
                transform: 'rotate(180deg)', height: 22, lineHeight: 1 }}>
                {labels[i]}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ThresholdSlider({ sweep, currentFar, currentFrr, eer }) {
  const [tIdx, setTIdx] = useState(() => {
    const idx = sweep.findIndex(s => Math.round(s.t * 100) === 45);
    return idx >= 0 ? idx : 15;
  });
  const entry = sweep[tIdx] || {};
  const col = (v) => v == null ? 'var(--text-muted)' : v < 0.05 ? '#22c55e' : v < 0.15 ? '#f59e0b' : '#ef4444';
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <span style={{ fontSize: 13, fontWeight: 700 }}>Threshold: {Math.round(entry.t * 100)}%</span>
        {eer && (
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
            EER at {Math.round(eer.t * 100)}%
            {' '}(FAR≈FRR≈{eer.far != null ? `${Math.round(eer.far * 100)}%` : '—'})
          </span>
        )}
      </div>
      <input type="range" min={0} max={sweep.length - 1} value={tIdx}
        onChange={e => setTIdx(Number(e.target.value))}
        style={{ width: '100%', marginBottom: 12 }} />
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, background: 'var(--surface-alt)', borderRadius: 8, padding: '10px 14px' }}>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 2 }}>FAR (security risk)</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: col(entry.far) }}>
            {entry.far != null ? `${(entry.far * 100).toFixed(1)}%` : '—'}
          </div>
          <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>unknowns let through</div>
        </div>
        <div style={{ flex: 1, background: 'var(--surface-alt)', borderRadius: 8, padding: '10px 14px' }}>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 2 }}>FRR (annoyance)</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: col(entry.frr) }}>
            {entry.frr != null ? `${(entry.frr * 100).toFixed(1)}%` : '—'}
          </div>
          <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>known people alerted</div>
        </div>
      </div>
    </div>
  );
}

function KnownTab() {
  const [preset,   setPreset]   = useState(30);
  const [dateFrom, setDateFrom] = useState(() => fmtRangeDate(new Date(Date.now() - 30 * 86400000)));
  const [dateTo,   setDateTo]   = useState(() => fmtRangeDate(new Date()));
  const [loading,  setLoading]  = useState(false);
  const [data,     setData]     = useState(null);
  const [error,    setError]    = useState('');

  const run = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await apiFetch(`/admin/known-analysis?${rangeQuery(preset, dateFrom, dateTo)}`);
      if (!r.ok) throw new Error(await r.text());
      setData(await r.json());
    } catch (e) { setError(String(e)); }
    finally { setLoading(false); }
  }, [preset, dateFrom, dateTo]);

  const hasData = data && data.total_labelled > 0;

  return (
    <div>
      <RangeToolbar preset={preset} setPreset={setPreset}
        dateFrom={dateFrom} setDateFrom={setDateFrom} dateTo={dateTo} setDateTo={setDateTo}
        onRun={run} loading={loading} />

      {error && (
        <div style={{ background: 'rgba(231,76,60,0.1)', border: '1px solid rgba(231,76,60,0.3)',
          borderRadius: 10, padding: '12px 16px', color: '#e74c3c', fontSize: 13, marginBottom: 16 }}>
          ⚠️ {error}
        </div>
      )}

      {loading && <div className="pk-loading">⏳ Running…</div>}

      {!loading && !data && !error && (
        <div className="pk-empty">
          <div className="pk-empty-icon">👤</div>
          <h3>Run analysis to see known-face metrics</h3>
          <p>Works on labelled data only — label entries in Known Seen first.</p>
        </div>
      )}

      {!loading && data && (
        <>
          {/* Labelled data notice */}
          <div style={{ padding: '10px 14px', background: 'var(--surface)', border: '1px solid var(--border)',
            borderRadius: 10, marginBottom: 14, display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: 13 }}>
              <strong>{data.total_labelled}</strong> labelled events used
            </span>
            {data.total_unlabelled > 0 && (
              <span style={{ fontSize: 12, color: '#f59e0b' }}>
                ⚠️ {data.total_unlabelled} unlabelled entries excluded — label them in Known Seen for better accuracy
              </span>
            )}
          </div>

          {!hasData ? (
            <div className="pk-empty" style={{ padding: '40px 20px' }}>
              <div className="pk-empty-icon">🏷️</div>
              <h3>No labelled data yet</h3>
              <p>Go to Known Seen and label suppression events as correct or wrong first.</p>
            </div>
          ) : (
            <>
              {/* Overview stats */}
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
                <StatCard label="correct suppressions" value={data.correct_suppressions} color="#22c55e" />
                <StatCard label="wrong suppressions"   value={data.wrong_suppressions}   color={data.wrong_suppressions > 0 ? '#ef4444' : 'var(--text)'} />
                <StatCard label="known people alerted" value={data.wrong_unknown_alerts}  color={data.wrong_unknown_alerts > 0 ? '#f59e0b' : 'var(--text)'} />
              </div>

              {/* FAR / FRR at current threshold */}
              <Section title="📊 FAR / FRR — Current Threshold" accent="#3498db">
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
                  <div style={{ flex: 1, background: 'var(--surface-alt)', borderRadius: 8, padding: '12px 16px' }}>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 2 }}>FAR — False Accept Rate</div>
                    <div style={{ fontSize: 28, fontWeight: 800,
                      color: data.far == null ? 'var(--text-muted)' : data.far < 0.02 ? '#22c55e' : data.far < 0.10 ? '#f59e0b' : '#ef4444' }}>
                      {data.far != null ? `${(data.far * 100).toFixed(1)}%` : '—'}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                      Unknowns wrongly suppressed ({data.wrong_suppressions} / {data.total_impostors})
                    </div>
                  </div>
                  <div style={{ flex: 1, background: 'var(--surface-alt)', borderRadius: 8, padding: '12px 16px' }}>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 2 }}>FRR — False Reject Rate</div>
                    <div style={{ fontSize: 28, fontWeight: 800,
                      color: data.frr == null ? 'var(--text-muted)' : data.frr < 0.05 ? '#22c55e' : data.frr < 0.20 ? '#f59e0b' : '#ef4444' }}>
                      {data.frr != null ? `${(data.frr * 100).toFixed(1)}%` : '—'}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                      Known people triggered alert ({data.wrong_unknown_alerts} / {data.total_known_appearances})
                    </div>
                  </div>
                </div>
                <p style={{ margin: 0, fontSize: 11, color: 'var(--text-muted)' }}>
                  Based on {data.genuine_count} genuine scores and {data.impostor_count} impostor scores from labelled events.
                </p>
              </Section>

              {/* Score distribution */}
              {(data.genuine_count > 0 || data.impostor_count > 0) && (
                <Section title="📈 Score Distribution — Genuine vs Impostor">
                  <ScoreDist genuineDist={data.genuine_dist} impostorDist={data.impostor_dist} />
                  <p style={{ margin: '8px 0 0', fontSize: 11, color: 'var(--text-muted)' }}>
                    Ideal: green bars cluster right (high scores), red bars cluster left. Overlap = gray zone where threshold matters.
                  </p>
                </Section>
              )}

              {/* Threshold slider */}
              {data.threshold_sweep?.length > 0 && (data.genuine_count > 0 || data.impostor_count > 0) && (
                <Section title="🎚 Threshold Explorer — drag to see FAR / FRR tradeoff">
                  <ThresholdSlider
                    sweep={data.threshold_sweep}
                    currentFar={data.far}
                    currentFrr={data.frr}
                    eer={data.eer}
                  />
                </Section>
              )}

              {/* Per-person table */}
              {data.persons?.length > 0 && (
                <Section title="👤 Per-Person Breakdown">
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid var(--border)' }}>
                          {['Person', 'Correct', 'Wrong', 'Alerted', 'Avg Score', 'Min', 'Max', 'Status'].map(h => (
                            <th key={h} style={{ padding: '6px 10px', textAlign: 'left', color: 'var(--text-muted)',
                              fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {data.persons.map(p => {
                          const status = p.wrong_suppressions >= 2 ? { text: '⚠ Re-enrol', color: '#ef4444' }
                            : p.missed_alerts >= 1                  ? { text: '⚠ Near-miss', color: '#f59e0b' }
                            : { text: '✓ Good', color: '#22c55e' };
                          return (
                            <tr key={p.name} style={{ borderBottom: '1px solid var(--border)' }}>
                              <td style={{ padding: '7px 10px', fontWeight: 700 }}>{p.name}</td>
                              <td style={{ padding: '7px 10px', color: '#22c55e', fontWeight: 600 }}>{p.correct_suppressions}</td>
                              <td style={{ padding: '7px 10px', color: p.wrong_suppressions > 0 ? '#ef4444' : 'var(--text-muted)', fontWeight: 600 }}>{p.wrong_suppressions}</td>
                              <td style={{ padding: '7px 10px', color: p.missed_alerts > 0 ? '#f59e0b' : 'var(--text-muted)', fontWeight: 600 }}>{p.missed_alerts}</td>
                              <td style={{ padding: '7px 10px' }}>{p.score_avg != null ? `${Math.round(p.score_avg * 100)}%` : '—'}</td>
                              <td style={{ padding: '7px 10px', color: 'var(--text-muted)' }}>{p.score_min != null ? `${Math.round(p.score_min * 100)}%` : '—'}</td>
                              <td style={{ padding: '7px 10px', color: 'var(--text-muted)' }}>{p.score_max != null ? `${Math.round(p.score_max * 100)}%` : '—'}</td>
                              <td style={{ padding: '7px 10px', fontWeight: 600, color: status.color }}>{status.text}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </Section>
              )}

              {/* Suggestions */}
              {data.suggestions?.length > 0 && (
                <Section title="💡 Suggestions" accent="#f59e0b">
                  {data.suggestions.map((s, i) => (
                    <div key={i} style={{ padding: '8px 10px', background: 'var(--surface-alt)',
                      borderRadius: 8, marginBottom: 8, fontSize: 13, borderLeft: '3px solid #f59e0b' }}>
                      {s.message}
                    </div>
                  ))}
                </Section>
              )}

              {data.suggestions?.length === 0 && (
                <div style={{ padding: '14px 16px', background: 'rgba(34,197,94,0.08)',
                  border: '1px solid rgba(34,197,94,0.3)', borderRadius: 10, fontSize: 13, color: '#22c55e' }}>
                  ✅ No issues found in labelled data — face recognition looks healthy.
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

// ─── Alerts (gate analysis) tab ───────────────────────────────────────────────

function GateFinding({ f, faReasons }) {
  const conf = f.confidence ? ` [${f.confidence}]` : '';
  let text = f.message;
  if (f.type === 'false_alerts' && f.by_reason) {
    const parts = Object.entries(f.by_reason).map(([r, c]) => `${(faReasons?.[r]) || r}: ${c}`);
    text = `${f.count} false alert(s) — ${parts.join(', ')}`;
  }
  const isWarn = /counterproductive|no_clean_split|not_blocking|blocking_humans|unverifiable/.test(f.type);
  const color  = isWarn ? '#e67e22' : 'var(--text-muted)';
  return (
    <div style={{ padding: '8px 10px', background: 'var(--surface-alt)', borderRadius: 8,
      marginBottom: 8, fontSize: 13, borderLeft: `3px solid ${color}` }}>
      {text}{conf && <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>{conf}</span>}
    </div>
  );
}

function GateSuggestion({ s }) {
  return (
    <div style={{ padding: '10px 12px', background: 'var(--surface-alt)', borderRadius: 8,
      marginBottom: 8, borderLeft: '3px solid #f59e0b' }}>
      <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 3 }}>
        💡 {s.reason_label || s.type}
        {s.confidence && <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 400, marginLeft: 6 }}>[{s.confidence}]</span>}
      </div>
      <div style={{ fontSize: 12, color: 'var(--text)', marginBottom: 4 }}>{s.reason}</div>
      {s.reasoning && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6 }}>{s.reasoning}</div>}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, flexWrap: 'wrap' }}>
        <code style={{ fontFamily: 'monospace', color: 'var(--text-muted)' }}>{s.param}</code>
        <span style={{ color: 'var(--text-muted)' }}>{JSON.stringify(s.cur)} →</span>
        <code style={{ fontFamily: 'monospace', color: '#22c55e', fontWeight: 700 }}>{JSON.stringify(s.new)}</code>
      </div>
      {s.tradeoff && <div style={{ fontSize: 11, color: '#e67e22', marginTop: 4 }}>{s.tradeoff}</div>}
    </div>
  );
}

function AlertsTab({ cameras }) {
  const [preset,   setPreset]   = useState(30);
  const [dateFrom, setDateFrom] = useState(() => fmtRangeDate(new Date(Date.now() - 30 * 86400000)));
  const [dateTo,   setDateTo]   = useState(() => fmtRangeDate(new Date()));
  const [selCam,   setSelCam]   = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [data,     setData]     = useState(null);
  const [error,    setError]    = useState('');

  const run = useCallback(async () => {
    setLoading(true); setError('');
    try {
      let url = `/admin/gate-analysis?${rangeQuery(preset, dateFrom, dateTo)}`;
      if (selCam) url += `&cam=${encodeURIComponent(selCam)}`;
      const r = await apiFetch(url);
      if (!r.ok) throw new Error(await r.text());
      setData(await r.json());
    } catch (e) { setError(String(e)); }
    finally { setLoading(false); }
  }, [preset, dateFrom, dateTo, selCam]);

  const t = data?.totals;
  const camsWithContent = (data?.analyses || []).filter(
    a => a.findings?.length || a.suggestions?.length || a.false_alerts || a.confirmed_missed);

  return (
    <div>
      <RangeToolbar preset={preset} setPreset={setPreset}
        dateFrom={dateFrom} setDateFrom={setDateFrom} dateTo={dateTo} setDateTo={setDateTo}
        onRun={run} loading={loading}>
        <CamPicker cameras={cameras} value={selCam} onChange={setSelCam} />
      </RangeToolbar>

      {error && (
        <div style={{ background: 'rgba(231,76,60,0.1)', border: '1px solid rgba(231,76,60,0.3)',
          borderRadius: 10, padding: '12px 16px', color: '#e74c3c', fontSize: 13, marginBottom: 16 }}>
          ⚠️ {error}
        </div>
      )}

      {loading && <div className="pk-loading">⏳ Running…</div>}

      {!loading && !data && !error && (
        <div className="pk-empty">
          <div className="pk-empty-icon">🚨</div>
          <h3>Run analysis on labelled alerts</h3>
          <p>Label fired alerts as correct / false in the Alerts page, then run this to see which
             gate let false alerts through — with suggested fixes.</p>
        </div>
      )}

      {!loading && data && t && (
        <>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
            <StatCard label="total fired"     value={t.total_alerts} />
            <StatCard label="✅ correct"       value={t.correct} color="#22c55e" />
            <StatCard label="🔴 false alert"   value={t.false_alerts}   color={t.false_alerts > 0 ? '#ef4444' : 'var(--text)'} />
            <StatCard label="⚠️ false trigger" value={t.false_trig_human} color={t.false_trig_human > 0 ? '#f59e0b' : 'var(--text)'} />
            <StatCard label="unreviewed"       value={t.unreviewed_alerts} color="var(--text-muted)" sub="not counted" />
            <StatCard label="period"           value={data.period_label} color="var(--text-muted)" />
          </div>

          {t.unreviewed_alerts > 0 && (
            <div style={{ padding: '10px 14px', background: 'rgba(245,158,11,0.1)',
              border: '1px solid rgba(245,158,11,0.3)', borderRadius: 10, marginBottom: 14,
              fontSize: 12, color: '#f59e0b' }}>
              ⚠️ {t.unreviewed_alerts} fired alert(s) not yet labelled — excluded from findings.
              Label them in Alerts (select all → Mark) for a complete picture.
            </div>
          )}

          {data.n_findings === 0 && data.n_suggestions === 0 ? (
            <div className="pk-empty" style={{ padding: '40px 20px' }}>
              <div className="pk-empty-icon">🏷️</div>
              <h3>No labelled alerts to analyse</h3>
              <p>Label some fired alerts as correct or false first, then re-run.</p>
            </div>
          ) : camsWithContent.map(a => (
            <Section key={a.cam_idx}
              title={`📷 ${a.cam_name} — ${a.false_alerts} false / ${a.correct} correct`}
              accent={a.false_alerts > 0 ? '#e74c3c' : undefined}>
              {a.findings?.length > 0 && (
                <div style={{ marginBottom: a.suggestions?.length ? 12 : 0 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)',
                    textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Findings</div>
                  {a.findings.map((f, i) => <GateFinding key={i} f={f} faReasons={data.fa_reasons_labels} />)}
                </div>
              )}
              {a.suggestions?.length > 0 && (
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)',
                    textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Suggested fixes</div>
                  {a.suggestions.map((s, i) => <GateSuggestion key={i} s={s} />)}
                </div>
              )}
            </Section>
          ))}
        </>
      )}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

const fmtDate = d => {
  const y  = d.getFullYear();
  const m  = String(d.getMonth() + 1).padStart(2, '0');
  const dy = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dy}`;
};

export default function ForensicReportPage() {
  const [tab,      setTab]      = useState('missed');  // 'missed' | 'known'
  const [cameras,  setCameras]  = useState([]);
  const [selCam,   setSelCam]   = useState(null);
  const [preset,   setPreset]   = useState(30);
  const [dateFrom, setDateFrom] = useState(() => fmtDate(new Date(Date.now() - 30 * 86400000)));
  const [dateTo,   setDateTo]   = useState(() => fmtDate(new Date()));
  const [loading,  setLoading]  = useState(false);
  const [report,   setReport]   = useState(null);
  const [error,    setError]    = useState('');

  useEffect(() => {
    apiFetch('/cameras')
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.cameras) setCameras(d.cameras.map(c => c.name || '').filter(Boolean)); })
      .catch(() => {});
  }, []);

  const run = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      let url;
      if (preset === -1) {
        const lo = Math.floor(new Date(dateFrom + 'T00:00:00').getTime() / 1000);
        const hi = Math.floor(new Date(dateTo   + 'T23:59:59').getTime() / 1000);
        url = `/admin/forensic-report?ts_lo=${lo}&ts_hi=${hi}`;
      } else {
        url = `/admin/forensic-report?days=${preset}`;
      }
      if (selCam) url += `&cam=${encodeURIComponent(selCam)}`;

      const r = await apiFetch(url);
      if (!r.ok) throw new Error(await r.text());
      const data = await r.json();
      if (data.cameras?.length) setCameras(data.cameras.filter(Boolean));
      setReport(data);
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  }, [preset, dateFrom, dateTo, selCam]);

  const cm = report?.confirmed_missed ?? 0;
  const cb = report?.correct_blocks   ?? 0;

  return (
    <div className="pk-page">

      {/* Header */}
      <div className="pk-header">
        <div className="pk-header-left">
          <h1>📊 Analysis</h1>
          <p>{tab === 'missed' ? 'Missed-alert breakdown — block reasons, Fix eligibility, safety check'
              : tab === 'alerts' ? 'Fired-alert gate analysis — which gate let false alerts through, with suggested fixes'
              : 'Known face recognition quality — FAR/FRR, score distribution, per-person'}</p>
        </div>
        {tab === 'missed' && report && (
          <div className="pk-header-right">
            <span className="pk-stat">
              <strong style={{ color: cm > 0 ? '#e74c3c' : undefined }}>{cm}</strong> missed
            </span>
            <span className="pk-stat">
              <strong style={{ color: cb > 0 ? '#27ae60' : undefined }}>{cb}</strong> correct blocks
            </span>
          </div>
        )}
      </div>

      {/* Tab switcher */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
        <button className={`pk-filter-tab${tab === 'missed' ? ' active' : ''}`} onClick={() => setTab('missed')}>
          📊 Missed Alerts
        </button>
        <button className={`pk-filter-tab${tab === 'alerts' ? ' active' : ''}`} onClick={() => setTab('alerts')}>
          🚨 Alerts
        </button>
        <button className={`pk-filter-tab${tab === 'known' ? ' active' : ''}`} onClick={() => setTab('known')}>
          👤 Known Faces
        </button>
      </div>

      {tab === 'known'  && <KnownTab />}
      {tab === 'alerts' && <AlertsTab cameras={cameras} />}

      {tab === 'missed' && <>
      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
        padding: '12px 0 16px' }}>
        <div style={{ display: 'flex', gap: 4 }}>
          {PRESETS.map(p => (
            <button key={p.days} className={`pk-filter-tab${preset === p.days ? ' active' : ''}`}
              onClick={() => setPreset(p.days)}>
              {p.label}
            </button>
          ))}
          <button className={`pk-filter-tab${preset === -1 ? ' active' : ''}`}
            onClick={() => setPreset(-1)}>
            Custom
          </button>
        </div>

        {preset === -1 && (
          <div className="pk-date-filter">
            <label>From</label>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
            <label>To</label>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} />
          </div>
        )}

        <CamPicker cameras={cameras} value={selCam} onChange={setSelCam} />

        <button className="pk-btn primary" onClick={run} disabled={loading}
          style={{ marginLeft: 'auto' }}>
          {loading ? '⏳ Running…' : '▶ Run Analysis'}
        </button>
      </div>

      {/* Error */}
      {error && (
        <div style={{ background: 'rgba(231,76,60,0.1)', border: '1px solid rgba(231,76,60,0.3)',
          borderRadius: 10, padding: '12px 16px', color: '#e74c3c', fontSize: 13, marginBottom: 16 }}>
          ⚠️ {error}
        </div>
      )}

      {loading && <div className="pk-loading">⏳ Running analysis…</div>}

      {!loading && !report && !error && (
        <div className="pk-empty">
          <div className="pk-empty-icon">📊</div>
          <h3>Select filters and run analysis</h3>
          <p>Choose a time range and optionally a camera, then click Run Analysis.</p>
        </div>
      )}

      {/* Results */}
      {!loading && report && (
        <div>

          {/* Overview */}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
            <StatCard label="confirmed_missed" value={cm}
              color={cm > 0 ? '#e74c3c' : 'var(--text)'} />
            <StatCard label="correct_block" value={cb}
              color={cb > 0 ? '#27ae60' : 'var(--text)'} />
            <StatCard label="unlabeled" value={report.unlabeled}
              color="var(--text-muted)" sub="excluded" />
            <StatCard label="period" value={report.period_label}
              color="var(--text-muted)" />
          </div>

          {/* Score ranges for confirmed_missed */}
          {cm > 0 && (report.cm_yolo || report.cm_clip) && (
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 14,
              padding: '10px 14px', background: 'var(--surface)',
              border: '1px solid var(--border)', borderRadius: 10 }}>
              <ScoreRange label="YOLO conf:" stats={report.cm_yolo} color="#f39c12" />
              <ScoreRange label="CLIP score:" stats={report.cm_clip} color="#3498db" />
            </div>
          )}

          {cm === 0 ? (
            <div className="pk-empty" style={{ padding: '40px 20px' }}>
              <div className="pk-empty-icon">✅</div>
              <h3>No confirmed_missed entries</h3>
              <p>Nothing to analyse in this period. Label more entries and re-run.</p>
            </div>
          ) : (
            <>
              {/* By camera */}
              {Object.keys(report.cam_counts).length > 0 && (
                <Section title="📷 By Camera — confirmed_missed">
                  {Object.entries(report.cam_counts).map(([cam, cnt]) => (
                    <BarRow key={cam} label={cam} count={cnt} total={cm} />
                  ))}
                </Section>
              )}

              {/* By alert rule */}
              {Object.keys(report.rule_counts).length > 0 && (
                <Section title="📋 By Alert Rule — confirmed_missed">
                  {Object.entries(report.rule_counts).map(([rule, cnt]) => {
                    const short = rule.replace('RULE_BLOCK_', '').replace('RULE_PASS_', 'PASS_');
                    return <BarRow key={rule} label={short} count={cnt} total={cm} />;
                  })}
                </Section>
              )}

              {/* By block reason — with expandable entry IDs */}
              {Object.keys(report.reason_counts).length > 0 && (
                <Section title="🔍 By Block Reason — confirmed_missed">
                  {Object.entries(report.reason_counts).map(([reason, cnt]) => (
                    <BarRow key={reason} label={reason} count={cnt} total={cm}
                      tag={reason === 'skeleton_no_path' ? 'Fix 4' : null}
                      entryIds={report.reason_entries?.[reason]} />
                  ))}
                </Section>
              )}

              {/* Fix 4 */}
              <Section title="🔧 Fix 4 — SKELETON_NO_PATH, all gates pass"
                accent={report.fix4_eligible > 0 ? '#e67e22' : undefined}>
                {report.fix4_eligible > 0 ? (
                  <>
                    <div style={{ display: 'flex', gap: 14, alignItems: 'center', marginBottom: 10 }}>
                      <span style={{ fontSize: 36, fontWeight: 800, color: '#e67e22' }}>
                        {report.fix4_eligible}
                      </span>
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>
                          entries would pass with Fix 4
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                          clothing ✓&nbsp;&nbsp;clip_human ✓&nbsp;&nbsp;non_osc ✓
                        </div>
                      </div>
                    </div>
                    {report.fix4_entry_ids?.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {report.fix4_entry_ids.map(id => (
                          <code key={id} style={{ fontSize: 10, padding: '2px 6px', borderRadius: 6,
                            background: 'rgba(230,126,34,0.1)', border: '1px solid rgba(230,126,34,0.3)',
                            color: '#e67e22' }}>
                            {id.replace(/^missed_/, '')}
                          </code>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                    No entries eligible for Fix 4 in this period.
                  </div>
                )}
              </Section>

              {/* CLIP failure clusters */}
              {report.clip_clusters?.length > 0 && (
                <Section title="🎯 CLIP Failure Clusters">
                  {report.clip_clusters.map(cl => (
                    <ClipClusterRow key={cl.cam} cl={cl} />
                  ))}
                </Section>
              )}
            </>
          )}

          {/* Safety check */}
          {(() => {
            const skelSafe   = report.cb_skel_true   === 0;
            const pathSafe   = (report.cb_path_risk   ?? 0) === 0;
            const notdirSafe = (report.cb_notdir_risk ?? 0) === 0;
            const allSafe    = skelSafe && pathSafe && notdirSafe;
            return (
              <Section title="🛡 Safety Check (correct_block)"
                accent={allSafe ? '#27ae60' : '#e74c3c'}>
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 12 }}>
                  {[
                    { label: 'ev_skeleton=True (Fix 4 risk)', val: report.cb_skel_true,   safe: skelSafe },
                    { label: 'path_short, all gates pass',    val: report.cb_path_risk ?? 0,   safe: pathSafe },
                    { label: 'not_directional, all gates pass', val: report.cb_notdir_risk ?? 0, safe: notdirSafe },
                    { label: 'ev_clip_human=True',            val: report.cb_clip_true,   safe: null },
                  ].map(({ label, val, safe }) => (
                    <div key={label} style={{ minWidth: 160 }}>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>{label}</div>
                      <div style={{ fontSize: 22, fontWeight: 800,
                        color: safe === null ? 'var(--text)' : safe ? '#27ae60' : '#e74c3c' }}>
                        {safe === true ? '✅ ' : safe === false ? '⚠️ ' : ''}{val}
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <div style={{ fontSize: 12, color: skelSafe ? '#27ae60' : '#e74c3c' }}>
                    {skelSafe
                      ? 'Fix 4 safe ✅'
                      : `⚠️ ${report.cb_skel_true} correct_block(s) have ev_skeleton=True — review before Fix 4!`}
                  </div>
                  <div style={{ fontSize: 12, color: pathSafe ? '#27ae60' : '#e74c3c' }}>
                    {pathSafe
                      ? 'path_short fix safe ✅'
                      : `⚠️ ${report.cb_path_risk} correct_block(s) would be at risk from path_short fix`}
                  </div>
                  <div style={{ fontSize: 12, color: notdirSafe ? '#27ae60' : '#e74c3c' }}>
                    {notdirSafe
                      ? 'not_directional fix safe ✅'
                      : `⚠️ ${report.cb_notdir_risk} correct_block(s) would be at risk from not_directional fix`}
                  </div>
                  {(report.al_correct ?? 0) > 0 && (
                    <div style={{ fontSize: 12, color: '#f39c12', marginTop: 4 }}>
                      ⚠️ {report.al_correct} correct fired alert(s) in window — verify any threshold tightening doesn't suppress them
                    </div>
                  )}
                </div>
              </Section>
            );
          })()}

          {/* Fired alerts */}
          <Section title="🚨 Fired Alerts (alert_log)">
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
              <StatCard label="total fired"    value={report.al_total}       color="var(--text)" />
              <StatCard label="unreviewed"     value={report.al_unreviewed}  color="var(--text-muted)" />
              <StatCard label="✅ correct"     value={report.al_correct}     color="#27ae60" />
              <StatCard label="🔴 false alert" value={report.al_false}       color="#e74c3c" />
              <StatCard label="⚠️ false trig"  value={report.al_false_human} color="#f39c12" />
            </div>

            {Object.keys(report.fa_cam_counts || {}).length > 0 && (
              <>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)',
                  textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
                  False alerts by camera
                </div>
                {Object.entries(report.fa_cam_counts).map(([cam, cnt]) => (
                  <BarRow key={cam} label={cam} count={cnt} total={report.al_false} />
                ))}
              </>
            )}

            {Object.keys(report.fa_reason_counts || {}).length > 0 && (
              <>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)',
                  textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: 16, marginBottom: 8 }}>
                  False alert reasons
                </div>
                {Object.entries(report.fa_reason_counts).map(([code, cnt]) => (
                  <BarRow key={code} label={FA_LABELS[code] || code} count={cnt} total={report.al_false} />
                ))}
              </>
            )}

            {report.al_total === 0 && (
              <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                No fired alerts in this period / camera.
              </div>
            )}
          </Section>

        </div>
      )}
      </>}
    </div>
  );
}

// ─── CLIP cluster expanded row ────────────────────────────────────────────────

function ClipClusterRow({ cl }) {
  const [expanded, setExpanded] = useState(false);

  const belowLabel = cl.below_threshold != null
    ? `${cl.below_threshold}/${cl.count} below threshold (${cl.threshold})`
    : null;
  const nearLabel  = cl.near_threshold > 0
    ? `${cl.near_threshold} near (within 0.005)`
    : null;

  return (
    <div style={{ borderBottom: '1px solid var(--border)', paddingBottom: 12, marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>
            {cl.cam}
            <span style={{ fontWeight: 400, color: 'var(--text-muted)', marginLeft: 8 }}>
              {cl.count} {cl.count === 1 ? 'entry' : 'entries'}
            </span>
          </div>

          {/* Score range */}
          {cl.score && (
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 4 }}>
              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                CLIP score:&nbsp;
                <code style={{ fontFamily: 'monospace', color: '#3498db' }}>
                  {cl.score.min.toFixed(3)} – {cl.score.max.toFixed(3)}
                </code>
                <span style={{ color: 'var(--text-muted)', marginLeft: 4 }}>
                  (mean {cl.score.mean.toFixed(3)})
                </span>
              </span>
              {cl.yolo && (
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                  YOLO:&nbsp;
                  <code style={{ fontFamily: 'monospace', color: '#f39c12' }}>
                    {cl.yolo.min.toFixed(2)} – {cl.yolo.max.toFixed(2)}
                  </code>
                </span>
              )}
            </div>
          )}

          {/* Body center X */}
          {cl.body_cx && (
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>
              body_center_x:&nbsp;
              <code style={{ fontFamily: 'monospace', color: 'var(--text)' }}>
                {Math.round(cl.body_cx.min)} – {Math.round(cl.body_cx.max)}
              </code>
              <span style={{ marginLeft: 4 }}>(mean {Math.round(cl.body_cx.mean)})</span>
              {cl.body_cy && (
                <span style={{ marginLeft: 12 }}>
                  body_center_y:&nbsp;
                  <code style={{ fontFamily: 'monospace', color: 'var(--text)' }}>
                    {Math.round(cl.body_cy.min)} – {Math.round(cl.body_cy.max)}
                  </code>
                </span>
              )}
            </div>
          )}

          {/* Threshold annotation */}
          {belowLabel && (
            <div style={{ fontSize: 11, marginBottom: 2 }}>
              <span style={{ color: cl.below_threshold > 0 ? '#e74c3c' : '#27ae60' }}>
                ● {belowLabel}
              </span>
              {nearLabel && (
                <span style={{ color: '#f39c12', marginLeft: 10 }}>● {nearLabel}</span>
              )}
            </div>
          )}
        </div>

        {cl.entry_ids?.length > 0 && (
          <button onClick={() => setExpanded(v => !v)}
            style={{ fontSize: 10, color: 'var(--primary)', background: 'transparent',
              border: 'none', cursor: 'pointer', whiteSpace: 'nowrap', paddingTop: 2 }}>
            {expanded ? '▲ hide' : `▼ entries`}
          </button>
        )}
      </div>

      {expanded && cl.entry_ids?.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
          {cl.entry_ids.map(id => (
            <code key={id} style={{ fontSize: 10, padding: '2px 6px', borderRadius: 6,
              background: 'rgba(52,152,219,0.08)', border: '1px solid rgba(52,152,219,0.25)',
              color: '#3498db' }}>
              {id.replace(/^missed_/, '')}
            </code>
          ))}
        </div>
      )}
    </div>
  );
}
